import { gql } from "graphql-request";
import { createGraphQLClient } from "./graphql.service";

export type AtLeastOne<T, U = { [K in keyof T]: Pick<T, K> }> = Partial<T> & U[keyof U];

export type ComponentTemplateTypes = "title-and-description" | "card-with-image";

export interface EntityViewComponent {
  name: string;
  description?: string;
  type: ComponentTemplateTypes;
  fields: Array<{
    field_name: string;
    description?: string;
    required?: boolean;
  }>;
  default_opts: {
    [key: string]: string;
  };
}

/** Component field → `${...}` template, e.g. `{ title: "${firstname} ${lastname ?? company}" }`. */
export type ComponentTemplates = Record<string, string>;

export interface EntityViewComponentMapping {
  id?: string;
  config: string | ComponentTemplates | Array<EntityViewComponentFieldMapping>;
  component: ComponentTemplateTypes;
  entity?: {
    data?: {
      id: string;
      name: string;
    };
  };
}

/** The mapping format before templates; still read from saved configs. */
export interface EntityViewComponentFieldMapping {
  type: "combine-spaces" | "fallback";
  componentField: string;
  entityFields: Array<string>;
}

export const toComponentTemplates = (
  config: EntityViewComponentMapping["config"] | undefined,
): ComponentTemplates => {
  const parsed: ComponentTemplates | Array<EntityViewComponentFieldMapping> =
    typeof config === "string" ? JSON.parse(config || "{}") : (config ?? {});
  if (!Array.isArray(parsed)) return parsed;
  return Object.fromEntries(
    parsed.map((mapping) => {
      const fields = mapping.entityFields.filter(Boolean);
      const template =
        mapping.type === "fallback"
          ? "${" + fields.join(" ?? ") + "}"
          : fields.map((field) => "${" + field + "}").join(" ");
      return [mapping.componentField, fields.length ? template : ""];
    }),
  );
};

export const getViewComponentMappings = async () => {
  const client = createGraphQLClient();
  const response = await client.request<{
    entityViewComponentMappingList: {
      data: Array<EntityViewComponentMapping>;
    };
  }>(gql`
    query getEntityDisplayComponent {
      entityViewComponentMappingList(pagination: { per_page: 10000 }) {
        data {
          id
          component
          config
          entity {
            data {
              id
              name
            }
          }
        }
      }
    }
  `);
  return response.entityViewComponentMappingList.data;
};

interface CreateUpdateViewComponentMappingInput {
  id?: string;
  config: string;
  component: string;
  entity_set?: {
    id: string;
  };
}

export const createViewComponentMapping = async ({
  input,
}: {
  input: CreateUpdateViewComponentMappingInput;
}) => {
  const client = createGraphQLClient();
  const response = await client.request<{
    createEntityViewComponentMapping: {
      data: { id: string };
    };
  }>(
    gql`
      mutation createEntityDisplayComponent($input: CreateEntityViewComponentMappingInput!) {
        createEntityViewComponentMapping(input: $input) {
          data {
            id
          }
        }
      }
    `,
    {
      input,
    },
  );
  return response.createEntityViewComponentMapping.data;
};

export const updateViewComponentMapping = async ({
  input,
}: {
  input: CreateUpdateViewComponentMappingInput;
}) => {
  const client = createGraphQLClient();
  const response = await client.request<{
    updateEntityViewComponentMapping: {
      data: { id: string };
    };
  }>(
    gql`
      mutation updateEntityDisplayComponent($input: UpdateEntityViewComponentMappingInput!) {
        updateEntityViewComponentMapping(input: $input) {
          data {
            id
          }
        }
      }
    `,
    {
      input,
    },
  );
  return response.updateEntityViewComponentMapping.data;
};
export const deleteViewComponentMapping = async (id: string) => {
  const client = createGraphQLClient();
  const response = await client.request<{
    deleteEntityViewComponentMapping: {
      data: { success: boolean };
    };
  }>(
    gql`
      mutation deleteEntityDisplayComponent($ids: [ID!]!) {
        deleteEntityViewComponentMapping(ids: $ids) {
          data {
            success
          }
        }
      }
    `,
    {
      ids: [id],
    },
  );
  return response.deleteEntityViewComponentMapping.data.success;
};
